// Rage-tap detector (UX §2 "Rage-tap (telemetry and context hint)", IMPLEMENTATION §2.9): ≥ `count` taps on
// ONE cell within `windowMs` with no effect (ui.json input.rageTapCount 3 / rageTapWindowMs 1000). PURE:
// the input layer (M6) feeds every board tap as {ms, x, y, effect} — `effect` = the tap changed something
// (a command that applied, a selection, a hit) — and logs a `rage_tap` row (telemetryRows.rageTapRow) for
// each detection.
// A streak = consecutive no-effect taps on the same cell, each within `windowMs` of the previous one. It fires
// ONCE, on the tap that completes `count` taps within `windowMs` (sliding window); further taps extend the
// same streak without firing again. A tap with an effect, a tap on another cell or a pause longer than
// `windowMs` ends the streak, so the next burst can fire again.

export interface TapSample {
  /** Wall or page ms (monotonic within one session). */
  ms: number;
  x: number;
  y: number;
  /** The tap did something (a command applied, a selection changed, a hit landed). */
  effect: boolean;
}

export interface RageTapConfig {
  /** Taps that make a rage tap (ui.json input.rageTapCount). */
  count: number;
  /** …within this many ms, first to last (ui.json input.rageTapWindowMs). */
  windowMs: number;
}

export interface RageTap {
  x: number;
  y: number;
  /** Time of the tap that completed the burst. */
  ms: number;
  /** Taps in the detecting window (= count). */
  taps: number;
  /** ms from the first to the last tap of that window. */
  spanMs: number;
}

export interface RageTapDetector {
  /** Feeds one tap; returns the rage tap it completes, else null. */
  tap(t: TapSample): RageTap | null;
  reset(): void;
}

export function createRageTapDetector(cfg: RageTapConfig): RageTapDetector {
  const count = Math.max(1, Math.floor(cfg.count));
  let cell: { x: number; y: number } | null = null;
  let times: number[] = [];
  let fired = false;
  const reset = (): void => {
    cell = null;
    times = [];
    fired = false;
  };
  return {
    reset,
    tap(t) {
      if (t.effect) {
        reset();
        return null;
      }
      const last = times[times.length - 1];
      const same = cell !== null && cell.x === t.x && cell.y === t.y;
      if (!same || last === undefined || t.ms - last > cfg.windowMs) {
        cell = { x: t.x, y: t.y };
        times = [];
        fired = false;
      }
      times.push(t.ms);
      if (times.length > count) times.splice(0, times.length - count);
      const first = times[0] as number;
      if (!fired && times.length >= count && t.ms - first <= cfg.windowMs) {
        fired = true;
        return { x: t.x, y: t.y, ms: t.ms, taps: times.length, spanMs: t.ms - first };
      }
      return null;
    },
  };
}

/** Every rage tap in a tap stream (e.g. `tap` rows of a telemetry export, or a test timeline). */
export function detectRageTaps(taps: readonly TapSample[], cfg: RageTapConfig): RageTap[] {
  const d = createRageTapDetector(cfg);
  const out: RageTap[] = [];
  for (const t of taps) {
    const r = d.tap(t);
    if (r) out.push(r);
  }
  return out;
}
